import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Edit2 } from 'lucide-react'
import api from '../../services/api'
import { formatPhone } from '../../utils/formatters'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import toast from 'react-hot-toast'

// Busca motoristas
const fetchDrivers = async () => {
  const response = await api.get('/api/drivers')
  return response.data.data
}

export default function DriversList() {
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['drivers'],
    queryFn: fetchDrivers,
  })

  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }) => api.patch(`/api/drivers/${id}/status`, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] })
    },
    onError: () => {
      toast.error('Erro ao atualizar status do motorista')
    },
  })

  // Filtro client-side: backend de drivers não expõe ?search (feature front-only)
  const term = search.trim().toLowerCase()
  const termDigits = term.replace(/\D/g, '')
  const filteredDrivers = (data?.drivers || []).filter((driver) => {
    if (!term) return true
    return (
      driver.name?.toLowerCase().includes(term) ||
      driver.email?.toLowerCase().includes(term) ||
      (termDigits && driver.phone?.includes(termDigits))
    )
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Motoristas</h1>
          <p className="page-subtitle">Gerencie os motoristas da sua equipe.</p>
        </div>
        <Link to="/drivers/new">
          <Button>
            <Plus className="w-4 h-4" />
            Novo Motorista
          </Button>
        </Link>
      </div>

      {/* Busca */}
      <Card padding="sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome, telefone ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border-0 focus:ring-0 text-gray-900 placeholder-gray-400"
          />
        </div>
      </Card>

      {/* Tabela */}
      <Card padding="none">
        {isLoading ? (
          <div className="p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : filteredDrivers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              {search ? 'Nenhum motorista encontrado' : 'Nenhum motorista cadastrado'}
            </p>
            {!search && (
              <Link to="/drivers/new">
                <Button variant="outline" size="sm">
                  <Plus className="w-4 h-4" />
                  Adicionar primeiro motorista
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200 text-sm text-gray-500">
                  <th className="px-4 py-3 font-medium">Nome</th>
                  <th className="px-4 py-3 font-medium">Telefone</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredDrivers.map((driver) => (
                  <tr key={driver.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">{driver.name}</td>
                    <td className="px-4 py-3 text-gray-600">{formatPhone(driver.phone)}</td>
                    <td className="px-4 py-3 text-gray-600">{driver.email}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          driver.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {driver.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={driver.isActive}
                          onClick={() => statusMutation.mutate({ id: driver.id, isActive: !driver.isActive })}
                          disabled={statusMutation.isPending && statusMutation.variables?.id === driver.id}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            driver.isActive ? 'bg-primary-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              driver.isActive ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/drivers/${driver.id}/edit`}>
                        <button className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
